import { GRANTS } from "@/other/constants/PrivilegeTypes.jsx";
import { VIEW_TYPES } from "@/other/constants/OtherKeys.jsx";
import { STORE_KEYS } from "@/other/constants/StoreKeys";
import {
  MarketingStackWorkflowStrip,
  marketingStackCardClassName,
  marketingStackContentInsetClassName,
  marketingStackPageClassName,
} from "@/react/components/marketingStack";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { useSwivuStore } from "@/react/hooks/useSwivuStore";
import { useCallback, useMemo, useState } from "react";
import { AnimatePresence, motion } from "motion/react";
import { showToast } from "@/other/utils/utils.jsx";
import { MarketingTag, MarketingTagType } from "@/bryntum-lib/models/MarketingTag";
import type { TemporalPhaseTag } from "@/react/components/marketing/temporal";
import {
  flattenFeedAssets,
  useSurfaceAssetMutations,
  useSurfaceFeedInfiniteQuery,
  useSurfaceProgressQuery,
  useSurfaceSolutionTagsQuery,
} from "../api/promoteSurface.api";
import type { PromoteSurfaceAsset, SurfaceTab, SurfaceWriteAction } from "../api/types";
import { assetTagIds, mergeTagIds } from "../utils/mergeTagIds";
import { getSharedActions } from "../utils/actionMatrix";
import PromoteSurfaceTabs from "./PromoteSurfaceTabs";
import PromoteSurfaceTabHint from "./PromoteSurfaceTabHint";
import PromoteSurfaceEmptyState from "./PromoteSurfaceEmptyState";
import PromoteSurfaceGrid from "./PromoteSurfaceGrid";
import PromoteSurfaceBulkAcceptSheet from "./PromoteSurfaceBulkAcceptSheet";
import PromoteSurfaceReviewOverlay from "./PromoteSurfaceReviewOverlay";

type Props = {
  boardUserAvailable: boolean;
};

function PromoteSurfaceGateMessage({ message }: { message: string }) {
  return (
    <div className={`${marketingStackPageClassName} w-full overflow-hidden bg-background`}>
      <MarketingStackWorkflowStrip activeView={VIEW_TYPES.promote} />
      <div className={marketingStackContentInsetClassName}>
        <div className={marketingStackCardClassName()}>
          <div className="flex flex-1 items-center justify-center p-10 text-center text-muted-foreground">
            {message}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function PromoteSurfaceView({ boardUserAvailable }: Props) {
  const { user, selectedBoard, project } = useSwivuBryntumContext();
  const boardSlug = (selectedBoard?.record as { slug?: string } | undefined)?.slug;
  const canView = Boolean(user?.can?.(GRANTS.grant_six));
  const effectiveBoardUserAvailable = boardUserAvailable;

  const { version: marketingTagStoreVersion } = useSwivuStore<MarketingTag>({
    storeId: STORE_KEYS.marketingTagStore,
  });

  const temporalTags = useMemo<TemporalPhaseTag[]>(() => {
    const records =
      (project?.marketingTagStore?.query(
        (t: MarketingTag) => t.type === MarketingTagType.TEMPORAL,
        true
      ) as MarketingTag[] | undefined) ?? [];
    return records.map((t) => ({
      id: t.id,
      label: t.label,
      description: t.description ?? undefined,
    }));
  }, [project, marketingTagStoreVersion]);

  const temporalTagIds = useMemo(() => temporalTags.map((t) => t.id), [temporalTags]);

  const [activeTab, setActiveTab] = useState<SurfaceTab>("review");
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [focusedAssetId, setFocusedAssetId] = useState<number | null>(null);
  const [overlayOpen, setOverlayOpen] = useState(false);
  const [bulkAcceptOpen, setBulkAcceptOpen] = useState(false);
  const [rangeAnchorId, setRangeAnchorId] = useState<number | null>(null);

  const progressQuery = useSurfaceProgressQuery(boardSlug, canView);
  const feedQuery = useSurfaceFeedInfiniteQuery(boardSlug, activeTab, canView);
  const tagsQuery = useSurfaceSolutionTagsQuery(boardSlug, canView);
  const { runSingle, runBulk, getAxiosMessage, invalidateSurfaceCaches } =
    useSurfaceAssetMutations(boardSlug, selectedBoard?.id);

  const assets = useMemo(() => flattenFeedAssets(feedQuery.data), [feedQuery.data]);
  const assetById = useMemo(() => new Map(assets.map((a) => [a.id, a])), [assets]);

  const selectedAssets = useMemo(
    () => selectedIds.map((id) => assetById.get(id)).filter(Boolean) as PromoteSurfaceAsset[],
    [selectedIds, assetById]
  );

  const sharedActions = useMemo(
    () => getSharedActions(selectedAssets.map((a) => a.verification_status)),
    [selectedAssets]
  );

  const clearSelection = useCallback(() => {
    setSelectedIds([]);
    setRangeAnchorId(null);
  }, []);

  const onTabChange = useCallback(
    (tab: SurfaceTab) => {
      setActiveTab(tab);
      clearSelection();
      setOverlayOpen(false);
      setFocusedAssetId(null);
    },
    [clearSelection]
  );

  const selectAsset = useCallback(
    (assetId: number, opts: { range?: boolean } = {}) => {
      const index = assets.findIndex((a) => a.id === assetId);
      if (index < 0) return;

      if (opts.range && rangeAnchorId != null) {
        const anchorIndex = assets.findIndex((a) => a.id === rangeAnchorId);
        if (anchorIndex >= 0) {
          const [from, to] = anchorIndex < index ? [anchorIndex, index] : [index, anchorIndex];
          setSelectedIds(assets.slice(from, to + 1).map((a) => a.id));
          setFocusedAssetId(assetId);
          return;
        }
      }

      // Default: multi-select toggle — no modifier key required.
      setSelectedIds((prev) =>
        prev.includes(assetId) ? prev.filter((id) => id !== assetId) : [...prev, assetId]
      );
      setRangeAnchorId(assetId);
      setFocusedAssetId(assetId);
    },
    [assets, rangeAnchorId]
  );

  const selectAllLoaded = useCallback(() => {
    setSelectedIds(assets.map((a) => a.id));
    setFocusedAssetId(assets[0]?.id ?? null);
  }, [assets]);

  const openOverlay = useCallback((assetId: number) => {
    setFocusedAssetId(assetId);
    setOverlayOpen(true);
  }, []);

  const closeOverlay = useCallback(() => {
    setOverlayOpen(false);
  }, []);

  const toastError = useCallback((error: unknown) => {
    showToast({ msg: getAxiosMessage(error), type: "error", duration: "medium" });
  }, [getAxiosMessage]);

  const runAction = useCallback(
    async (action: SurfaceWriteAction, assetIds: number[], tagIds?: number[]) => {
      if (!effectiveBoardUserAvailable && action !== "reprocess") {
        // Still allow attempt; server is source of truth — soft hint.
        showToast({ msg: "Board user required for this action. If it fails, ask an admin to add you to the board.",
          type: "warn", duration: "medium" });
      }

      try {
        if (assetIds.length === 1 && action !== "accept") {
          await runSingle.mutateAsync({ action, assetId: assetIds[0] });
        } else if (assetIds.length === 1 && action === "accept") {
          await runSingle.mutateAsync({
            action,
            assetId: assetIds[0],
            body: { tag_ids: tagIds ?? [] },
          });
        } else {
          const result = await runBulk.mutateAsync({
            action,
            asset_ids: assetIds,
            tag_ids: tagIds,
          });
          const failed = (result.results ?? []).filter((r) => !r.ok);
          const succeeded = (result.results ?? []).filter((r) => r.ok).map((r) => r.asset_id);
          if (failed.length) {
            showToast({
              msg: `${succeeded.length} succeeded, ${failed.length} failed`,
              type: "warn",
              duration: "medium",
            });
            setSelectedIds(failed.map((f) => f.asset_id));
          } else {
            clearSelection();
            showToast({ msg: `Updated ${assetIds.length} assets`, type: "success", duration: "medium" });
          }
          return;
        }

        if (action === "reprocess") {
          showToast({ msg: "Re-queued for AI", type: "success", duration: "medium" });
        }
        clearSelection();
      } catch (error) {
        toastError(error);
      }
    },
    [effectiveBoardUserAvailable, clearSelection, runBulk, runSingle, toastError]
  );

  const onBulkAccept = useCallback(
    async (payload: { solutionTagIds: number[]; temporalTagId: number | null }) => {
      const tags = tagsQuery.data ?? [];
      const mergeOpts = {
        temporalTagId: payload.temporalTagId,
        temporalTagIds,
      };
      try {
        if (selectedAssets.length === 1) {
          const asset = selectedAssets[0];
          const tag_ids = mergeTagIds(
            assetTagIds(asset),
            payload.solutionTagIds,
            tags,
            mergeOpts
          );
          await runAction("accept", [asset.id], tag_ids);
        } else {
          const results = await Promise.allSettled(
            selectedAssets.map((asset) =>
              runSingle.mutateAsync({
                action: "accept",
                assetId: asset.id,
                skipInvalidate: true,
                body: {
                  tag_ids: mergeTagIds(
                    assetTagIds(asset),
                    payload.solutionTagIds,
                    tags,
                    mergeOpts
                  ),
                },
              })
            )
          );
          await invalidateSurfaceCaches();
          const failedIds: number[] = [];
          results.forEach((r, i) => {
            if (r.status === "rejected") failedIds.push(selectedAssets[i].id);
          });
          if (failedIds.length) {
            showToast({
              msg: `${selectedAssets.length - failedIds.length} succeeded, ${failedIds.length} failed`,
              type: "warn",
              duration: "medium",
            });
            setSelectedIds(failedIds);
          } else {
            clearSelection();
            showToast({ msg: `Accepted ${selectedAssets.length} assets`, type: "success", duration: "medium" });
          }
        }
        setBulkAcceptOpen(false);
      } catch (error) {
        toastError(error);
      }
    },
    [
      clearSelection,
      invalidateSurfaceCaches,
      runAction,
      runSingle,
      selectedAssets,
      tagsQuery.data,
      temporalTagIds,
      toastError,
    ]
  );

  const overlayIndex = useMemo(() => {
    if (focusedAssetId == null) return 0;
    const idx = assets.findIndex((a) => a.id === focusedAssetId);
    return idx >= 0 ? idx : 0;
  }, [assets, focusedAssetId]);

  if (!canView) {
    return (
      <PromoteSurfaceGateMessage message="You need marketing (G6) access to open Promote." />
    );
  }

  if (!boardSlug) {
    return <PromoteSurfaceGateMessage message="Select a board to load the Promote queue." />;
  }

  const advanceOrCloseOverlay = (currentAssetId: number) => {
    const idx = assets.findIndex((a) => a.id === currentAssetId);
    const next = (idx >= 0 ? assets[idx + 1] : undefined) ?? (idx > 0 ? assets[idx - 1] : undefined);
    if (next && next.id !== currentAssetId) {
      setFocusedAssetId(next.id);
    } else {
      closeOverlay();
      setFocusedAssetId(null);
    }
  };

  const isEmpty = !feedQuery.isLoading && assets.length === 0;
  const isTabSwitching = Boolean(feedQuery.isFetching && feedQuery.isPlaceholderData);
  const showHint = !isEmpty || isTabSwitching;

  return (
    <div className={`promote-surface-page ${marketingStackPageClassName} w-full overflow-hidden bg-background`}>
      <MarketingStackWorkflowStrip activeView={VIEW_TYPES.promote} />
      <div className={marketingStackContentInsetClassName}>
        <div className={marketingStackCardClassName()}>
          <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden">
            <PromoteSurfaceTabs
              activeTab={activeTab}
              onChange={onTabChange}
              progress={progressQuery.data}
              loadedCount={isEmpty || isTabSwitching ? 0 : assets.length}
              onSelectAllLoaded={selectAllLoaded}
              selection={
                !overlayOpen
                  ? {
                      count: selectedIds.length,
                      actions: sharedActions,
                      busy: runSingle.isPending || runBulk.isPending,
                      onAccept: () => setBulkAcceptOpen(true),
                      onReject: () => runAction("reject", selectedIds),
                      onSkip: () => runAction("skip", selectedIds),
                      onClear: clearSelection,
                    }
                  : null
              }
            />

            <div className="min-h-[1.25rem] shrink-0">
              {showHint && <PromoteSurfaceTabHint tab={activeTab} />}
            </div>

            <div className="relative min-h-0 flex-1 overflow-hidden">
              <AnimatePresence mode="wait" initial={false}>
                <motion.div
                  key={activeTab}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -6 }}
                  transition={{ duration: 0.16, ease: "easeOut" }}
                  className="absolute inset-0"
                >
                  {feedQuery.isError ? (
                    <div className="flex h-full flex-col items-center justify-center gap-3 p-8">
                      <p className="text-sm text-destructive">Failed to load feed.</p>
                      <button
                        type="button"
                        className="text-sm underline"
                        onClick={() => feedQuery.refetch()}
                      >
                        Retry
                      </button>
                    </div>
                  ) : isEmpty && !isTabSwitching ? (
                    <PromoteSurfaceEmptyState tab={activeTab} progress={progressQuery.data} />
                  ) : (
                    <PromoteSurfaceGrid
                      assets={isTabSwitching ? [] : assets}
                      selectedIds={selectedIds}
                      focusedAssetId={focusedAssetId}
                      isLoading={feedQuery.isLoading || isTabSwitching}
                      isFetchingNextPage={feedQuery.isFetchingNextPage}
                      hasNextPage={Boolean(feedQuery.hasNextPage)}
                      fetchNextPage={() => feedQuery.fetchNextPage()}
                      onSelect={selectAsset}
                      onOpen={openOverlay}
                      tab={activeTab}
                    />
                  )}
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>

      <PromoteSurfaceBulkAcceptSheet
        open={bulkAcceptOpen}
        onOpenChange={setBulkAcceptOpen}
        tags={tagsQuery.data ?? []}
        temporalTags={temporalTags}
        busy={runSingle.isPending || runBulk.isPending}
        selectionCount={selectedIds.length}
        onConfirm={onBulkAccept}
      />

      <PromoteSurfaceReviewOverlay
        open={overlayOpen}
        assets={assets}
        index={overlayIndex}
        tags={tagsQuery.data ?? []}
        temporalTags={temporalTags}
        boardUserAvailable={effectiveBoardUserAvailable}
        busy={runSingle.isPending}
        onOpenChange={(open) => {
          if (!open) closeOverlay();
          else setOverlayOpen(true);
        }}
        onIndexChange={(idx) => {
          const asset = assets[idx];
          if (asset) setFocusedAssetId(asset.id);
        }}
        onAccept={async (asset, solutionTagIds, extras) => {
          const { temporalTagId, ...rest } = extras ?? {};
          const tag_ids = mergeTagIds(
            assetTagIds(asset),
            solutionTagIds,
            tagsQuery.data ?? [],
            { temporalTagId, temporalTagIds }
          );
          try {
            await runSingle.mutateAsync({
              action: "accept",
              assetId: asset.id,
              body: { tag_ids, ...rest },
            });
            advanceOrCloseOverlay(asset.id);
          } catch (error) {
            toastError(error);
          }
        }}
        onAction={async (action, asset) => {
          try {
            await runSingle.mutateAsync({ action, assetId: asset.id });
            if (action === "reprocess") {
              showToast({ msg: "Re-queued for AI", type: "success", duration: "medium" });
            }
            advanceOrCloseOverlay(asset.id);
          } catch (error) {
            toastError(error);
          }
        }}
      />
    </div>
  );
}
